<template>
  <v-dialog v-model="dialog" width="800" :persistent="true">
    <template v-slot:activator="{ on }">
      <permission-button color="primary" :container-class="`pull-right`" :button-class="`ml-0 mr-0`" @click="open()"
                         :permission="`patient_edit`"
                         :tooltip="{top: true, bottom: false, attach: false}"
                         v-if="editMode"
      >
        {{ 'app.edit' | trans }}
      </permission-button>
      <permission-button color="primary" :button-class="`ml-0 mr-0`" @click="open(true)"
                         :permission="canCreate"
                         :tooltip="{right: true, bottom: false, attach: false}"
                         v-else
      >
        {{ 'app.add_patient' | trans }}
      </permission-button>
    </template>
    <v-card>
      <v-toolbar card dark color="primary">
        <v-toolbar-title>{{ modalTitle }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-toolbar-items>
          <v-btn icon dark small @click="close">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <v-card-text>
        <v-container grid-list-md no-padding>
          <v-form ref="form" v-model="valid" lazy-validation>

            <v-layout row wrap>
              <v-flex xs6>
                <v-text-field
                    :label="'app.first_name' | trans"
                    clearable
                    v-model="patientEdited.first_name"
                    :rules="rules.first_name"
                    full
                ></v-text-field>
              </v-flex>
              <v-flex xs6>
                <v-text-field
                    :label="'app.surname' | trans"
                    clearable
                    v-model="patientEdited.last_name"
                    :rules="rules.last_name"
                    full
                ></v-text-field>
              </v-flex>
              <v-flex xs6>
                <v-text-field
                    :label="'app.age' | trans"
                    clearable
                    v-model="patientEdited.age"
                    :rules="rules.age"
                    full
                    type="number"
                ></v-text-field>
              </v-flex>
              <v-flex xs6>
                <v-radio-group v-model="patientEdited.sex" :column="false" :rules="rules.sex">
                  <v-radio
                      v-for="sex in ['male', 'female']"
                      :label="`app.${sex}` | trans"
                      :key="sex"
                      :value="sex"
                      :color="`success`"
                  ></v-radio>
                </v-radio-group>
              </v-flex>
              <v-flex xs6>
                <v-text-field
                    :label="'app.phone_number' | trans"
                    clearable
                    v-model="patientEdited.phone_number"
                    :rules="rules.phone_number"
                    full
                ></v-text-field>
              </v-flex>
              <v-flex xs6>
                <v-text-field
                    :label="'app.email' | trans"
                    clearable
                    v-model="patientEdited.email"
                    :rules="rules.email"
                    full
                    type="email"
                    :error-messages="emailErrors"
                ></v-text-field>
              </v-flex>
              <v-flex xs4>
                <v-text-field
                    :label="'app.street' | trans"
                    clearable
                    v-model="patientEdited.street"
                    :rules="rules.street"
                    full
                ></v-text-field>
              </v-flex>
              <v-flex xs4>
                <v-text-field
                    :label="'app.building_number' | trans"
                    clearable
                    v-model="patientEdited.building_number"
                    :rules="rules.building_number"
                    full
                ></v-text-field>
              </v-flex>
              <v-flex xs4>
                <v-text-field
                    :label="'app.apartment_number' | trans"
                    clearable
                    v-model="patientEdited.apartment_number"
                    :rules="rules.apartment_number"
                    full
                ></v-text-field>
              </v-flex>
              <v-flex xs6>
                <v-text-field
                    :label="'app.postal_code' | trans"
                    clearable
                    v-model="patientEdited.postal_code"
                    :rules="rules.postal_code"
                    full
                ></v-text-field>
              </v-flex>
              <v-flex xs6>
                <v-text-field
                    :label="'app.city' | trans"
                    clearable
                    v-model="patientEdited.city"
                    :rules="rules.city"
                    full
                ></v-text-field>
              </v-flex>
              <v-flex xs6>
                <v-select
                    v-model="patientEdited.state"
                    :items="states"
                    :label="'app.state' | trans"
                    :item-text="'name'"
                    :item-value="'id'"
                ></v-select>
              </v-flex>
              <v-flex xs6>
                <v-text-field
                    :label="'app.country' | trans"
                    clearable
                    v-model="patientEdited.country"
                    :rules="rules.country"
                    full
                ></v-text-field>
              </v-flex>
            </v-layout>
          </v-form>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" flat @click="close">
          {{ 'app.close' | trans }}
        </v-btn>
        <v-btn color="primary" @click="save(false)" :disabled="!valid" :loading="loading">
          {{ 'app.save' | trans }}
        </v-btn>
        <v-btn v-if="showRedirectButton" color="primary" @click="save(true)" :disabled="!valid" :loading="redirectButtonLoading">
          {{ 'app.save_and_redirect' | trans }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {trans} from "../../../../app";
import VoivodeshipService from "../../../../services/VoivodeshipService";
import {mapActions, mapGetters} from "vuex";
import {helpers} from "../../../../helpers";
import RulesBuilder from "../../../../utils/rulesBuilder";
import {DIETITIAN_FREE, DIETITIAN_TEST} from "../../../../utils/roles";

export default {
  props: {
    editMode: {
      type: Boolean,
      default: false,
    },
    showRedirectButton: false,
  },
  data: function () {
    return {
      dialog: false,
      valid: false,
      loading: false,
      redirectButtonLoading: false,
      patientEdited: {
        id: null,
        sex: '',
        first_name: '',
        last_name: '',
        age: '',
        phone_number: '',
        email: '',
        street: '',
        building_number: '',
        apartment_number: '',
        postal_code: '',
        city: '',
        state: '',
        country: '',
      },
      states: [],
      rulesBuilder: new RulesBuilder(),
      emailErrors: [],
    };
  },
  created() {
    this.states = new VoivodeshipService().getAll();
  },
  methods: {
    ...mapActions(['updatePatient', 'savePatient', 'fetchPatientsCount']),
    open(initialState = false) {
      this.emailErrors = [];
      this.$refs.form.reset();
      this.dialog = true;
      if (initialState) {
        this.patientEdited = {
          id: null,
          sex: '',
          first_name: '',
          last_name: '',
          age: '',
          phone_number: '',
          email: '',
          street: '',
          building_number: '',
          apartment_number: '',
          postal_code: '',
          city: '',
          state: '',
          country: '',
        };
      } else {
        this.patientEdited = helpers.deepClone(this.patient);
      }
    },
    close() {
      this.emailErrors = [];
      this.$refs.form.reset();
      this.dialog = false;
    },
    async save(goToPatient) {
      this.emailErrors = [];
      if (this.$refs.form.validate() === false) {
        return;
      }
      goToPatient ? this.redirectButtonLoading = true : this.loading = true;

      let patientId;
      try {
        if (this.patientEdited.id) {
          await this.updatePatient(this.patientEdited);
          patientId = this.patientEdited.id;
        } else {
          await this.savePatient(this.patientEdited);
          await this.fetchPatientsCount();
          patientId = this.patient.id;
        }
      } catch (error) {
        if (error.response && error.response.status === 422) {
          if(error.response.data?.errors?.email) {
            this.emailErrors = error.response.data?.errors?.email
          } else {
            this.$toast.error(trans('app.save_failed'));
          }
        } else {
          this.sentryCaptureException(error);
        }
        this.loading = false;
        this.redirectButtonLoading = false;
        return
      }

      this.loading = false;
      this.redirectButtonLoading = false;

      if (goToPatient) {
        window.location = `/patients/${patientId}/panel`;
        return;
      }

      this.showInfo(!!this.patientEdited.id)
      this.$emit('patient-saved');
      this.loading = false;
      this.close();
    },
    showInfo(patientEdited) {
      if (patientEdited) {
        this.$toast.success(trans('app.actions.edited', {name: trans('app.patient_accusative')}));
        return;
      }
      this.$toast.success(trans('app.actions.added', {name: trans('app.patient_accusative')}));
    },
    canCreate() {
        if ((this.userRole === DIETITIAN_FREE || this.userRole === DIETITIAN_TEST) && this.patientsCount >= 5) {
            return false;
        }
        return this.hasPermission('patient_create');
    }
  },
  computed: {
    ...mapGetters(['patient', 'userRole', 'patientsCount', 'hasPermission']),
    modalTitle: function () {
      const key = this.patientEdited && this.patientEdited.id
          ? 'app.patient_information.modal.edit.title'
          : 'app.patient_information.modal.add.title';

      return trans(key);
    },
    rules: function () {
      return {
        first_name: [
          ...this.rulesBuilder
              .addRequiredRule(trans('app.first_name'))
              .addMaxLengthRule(trans('app.first_name'), 255)
              .getRules()
        ],
        last_name: [
          ...this.rulesBuilder
              .addRequiredRule(trans('app.surname'))
              .addMaxLengthRule(trans('app.surname'), 255)
              .getRules()
        ],
        sex: [
          ...this.rulesBuilder
              .addRequiredRule(trans('app.sex'))
              .getRules()
        ],
        age: [
          ...this.rulesBuilder
              .addRequiredRule(trans('app.age'))
              .addIsIntegerRule(trans('app.age'))
              .addNumberBetweenRule(trans('app.age'), 1, 100)
              .getRules()
        ],
        phone_number: [
          ...this.rulesBuilder
              .addMaxLengthRule(trans('app.phone_number'), 255)
              .getRules()
        ],
        email: [
          ...this.rulesBuilder
              .addIsEmailRule(trans('app.email'))
              .addMaxLengthRule(trans('app.email'), 255)
              .getRules()
        ],
        street: [
          ...this.rulesBuilder
              .addMaxLengthRule(trans('app.street'), 255)
              .getRules()
        ],
        building_number: [
          ...this.rulesBuilder
              .addMaxLengthRule(trans('app.building_number'), 255)
              .getRules()
        ],
        apartment_number: [
          ...this.rulesBuilder
              .addMaxLengthRule(trans('app.apartment_number'), 255)
              .getRules()
        ],
        postal_code: [
          ...this.rulesBuilder
              .addMaxLengthRule(trans('app.postal_code'), 255)
              .getRules()
        ],
        city: [
          ...this.rulesBuilder
              .addMaxLengthRule(trans('app.city'), 255)
              .getRules()
        ],
        country: [
          ...this.rulesBuilder
              .addMaxLengthRule(trans('app.country'), 255)
              .getRules()
        ],
      };
    },
  },
  watch: {
    'patientEdited.email'() {
      this.emailErrors = [];
    }
  }
}
</script>
